import { Button, Icon, Spin, Tabs } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import AddTariffsModal from '../../components/modals/AddTariffsModal/AddTariffsModal';
import RoomModal from '../../components/modals/RoomModal/RoomModal';
import MetersTable from '../../components/tables/MetersTable/MetersTable';
import BackButton from '../../components/BackButton/BackButton';
import queries from '../../queries/queries';
import useFacility from '../../queries/useFacility';
import AboutFacility from '../facilityTabs/about/AboutFacility/AboutFacility';
import RoomsList from '../facilityTabs/RoomsList/RoomsList';
import FacilityTariffs from '../facilityTabs/tariffs/FacilityTariffs/FacilityTariffs';
import FacilityModal from '../../components/modals/facilityModal/FacilityModal/FacilityModal';
import useFacilityTariffs from '../../queries/useFacilityTariffs';
import { useSelector } from 'react-redux';
import useSetRefreshFn from '../../../../hooks/useSetRefreshFn';
import styles from './Facility.module.scss';

const tapBarStyle = {
  color: '#7CCDFF',
  fontWeight: 'bold',
  borderBottom: '1px solid #7CCDFF',
};

const Facility = () => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const params = useParams();
  const userCanCreate = useSelector(
    (store) => store.authentication.userInfo.CanCreateOffice
  );
  const { data: facility, isFetching, status } = useFacility(params.facilityId);
  const { data: facilityTariffs } = useFacilityTariffs(params.facilityId);
  const [editingFacility, setEditingFacility] = useState(null);
  const [isAddRoomModalVisible, setIsAddRoomModalVisible] = useState(false);
  const [isAddTariffsModalVisible, setIsAddTariffsModalVisible] =
    useState(false);
  const [queryKeys, setQueryKeys] = useState([]);

  useEffect(() => {
    if (!params.facilityTab) {
      history.push(`${params.facilityId}/rooms`);
    }
  }, [params.facilityTab, params.facilityId, history]);

  useEffect(() => {
    setQueryKeys((prev) =>
      prev.concat([
        [queries.facility, params.facilityId],
        [queries.facilityTariffs, params.facilityId],
      ])
    );
  }, [params.facilityId]);

  useSetRefreshFn(() => {
    queryKeys.forEach((key) => queryClient.invalidateQueries(key));
  }, [queryKeys]);

  const changeTab = useCallback(
    (key) => {
      history.push(`/Offices/facilities/${params.facilityId}/${key}`);
    },
    [params.facilityId, history]
  );

  const clickAddRoom = useCallback(() => {
    setIsAddRoomModalVisible(true);
  }, []);

  const clickCancelAddRoomModal = useCallback(() => {
    setIsAddRoomModalVisible(false);
  }, []);

  const clickSubmitAddRoomModal = useCallback(() => {
    setIsAddRoomModalVisible(false);
  }, []);

  const clickEditTariffs = useCallback(() => {
    setIsAddTariffsModalVisible(true);
  }, []);

  const clickCancelEditTariffs = useCallback(() => {
    setIsAddTariffsModalVisible(false);
  }, []);

  const clickEditFacility = useCallback(
    () => setEditingFacility(facility),
    [facility]
  );
  const closeEditing = useCallback(
    (edited) => {
      setEditingFacility(null);
      if (edited) {
        queryClient.invalidateQueries([queries.facility, params.facilityId]);
        queryClient.invalidateQueries([
          queries.facilityTariffs,
          params.facilityId,
        ]);
      }
    },
    [queryClient, params.facilityId]
  );

  const clickSubmitEditTariffs = useCallback(() => {
    setIsAddTariffsModalVisible(false);
    queryClient.invalidateQueries([queries.facilityTariffs, params.facilityId]);
  }, [queryClient, params.facilityId]);

  const editingFacilityWithTariffs = useMemo(
    () => ({ ...editingFacility, tariffs: facilityTariffs }),
    [editingFacility, facilityTariffs]
  );

  const handleBackButtonClick = () => {
    history.push('/Offices/facilities');
  };

  if (status === 'success' && !facility?.name && !isFetching) {
    return (
      <div className={styles.facilityLayout}>
        <div className={styles.facility}>
          <div className={styles.header}>
            <div className={styles.address}>
              <span className={styles.name}>Объект не найден</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Spin spinning={isFetching}>
      <div className={styles.facilityLayout}>
        <div className={styles.facility}>
          <div className={styles.header}>
            <BackButton
              className={styles.backBtn}
              onClick={handleBackButtonClick}
            />
            <div className={styles.address}>
              <span className={styles.name}>{facility?.name}</span>
              <span title={facility.house?.fullAddress}>
                {facility.house?.fullAddress && '/'}
                {facility.house?.fullAddress}
              </span>
            </div>
            <div className={styles.buttons}>
              {userCanCreate && (
                <>
                  <Button
                    shape="round"
                    className={cn(styles.addRoom, styles.transparent)}
                    onClick={clickEditFacility}
                  >
                    Редактировать объект
                  </Button>
                  {params.facilityTab === 'tariffs' && (
                    <Button
                      shape="round"
                      className={cn(styles.addRoom, styles.transparent)}
                      onClick={clickEditTariffs}
                    >
                      Добавить тарифы
                    </Button>
                  )}
                  <Button
                    shape="round"
                    className={cn(styles.addRoom, styles.colored)}
                    onClick={clickAddRoom}
                  >
                    Добавить помещение{' '}
                    <Icon type="plus" className={styles.icon} />
                  </Button>
                </>
              )}
            </div>
            <RoomModal
              visible={isAddRoomModalVisible}
              onCancel={clickCancelAddRoomModal}
              onOk={clickSubmitAddRoomModal}
            />
            <AddTariffsModal
              visible={isAddTariffsModalVisible}
              onCancel={clickCancelEditTariffs}
              onOk={clickSubmitEditTariffs}
            />
          </div>
          <div className={styles.tabsList}>
            <Tabs
              tabBarStyle={tapBarStyle}
              tabBarGutter={0}
              onChange={changeTab}
              activeKey={params.facilityTab}
            >
              <Tabs.TabPane
                tab="Список помещений"
                key="rooms"
                className={styles.tabContent}
              >
                <RoomsList setQueryKeys={setQueryKeys} />
              </Tabs.TabPane>
              <Tabs.TabPane
                tab="Счетчики"
                key="meters"
                className={styles.tabContent}
              >
                <MetersTable setQueryKeys={setQueryKeys} />
              </Tabs.TabPane>
              <Tabs.TabPane
                tab="Тарифы"
                key="tariffs"
                className={styles.tabContent}
              >
                <FacilityTariffs
                  facility={facility}
                  setQueryKeys={setQueryKeys}
                />
              </Tabs.TabPane>
              <Tabs.TabPane
                tab="Об объекте"
                key="about"
                className={styles.tabContent}
              >
                <AboutFacility facility={facility} />
              </Tabs.TabPane>
            </Tabs>
          </div>
        </div>
      </div>
      {editingFacility && (
        <FacilityModal
          visible={!!editingFacility}
          onOk={closeEditing}
          onCancel={closeEditing}
          editingFacility={editingFacilityWithTariffs}
        />
      )}
    </Spin>
  );
};

export default Facility;
